import { motion } from "framer-motion";
import {
  FolderGit2,
  GitBranch,
  Cpu,
  ListTodo,
  ArrowLeftRight,
  Plus,
  Play,
  ShieldCheck,
  FlaskConical,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useSampleDataCta } from "@/hooks/useSampleDataCta";
import { APPS } from "@/data/apps";
import { WS_ENV_LABEL, WS_STATUS_LABEL, WS_STATUS_TONE } from "@/data/workspaces";
import { Badge, Dot } from "@/components/ui/primitives";
import { SampleBadge } from "@/components/ui/SampleBadge";

const accent = APPS.workspaces.accent;

/** Compact desktop surface — the active cloud workspace at a glance. */
export function WorkspacesWidget() {
  const openApp = useHive((s) => s.openApp);
  const setWsWizard = useHive((s) => s.setWsWizard);
  const workspaceAction = useHive((s) => s.workspaceAction);
  const workspaces = useHive((s) => s.codeWorkspaces);
  const activeId = useHive((s) => s.activeWorkspaceId);
  const sampleCta = useSampleDataCta();
  const ws = workspaces.find((w) => w.id === activeId) ?? workspaces[0];

  const stop = (e: React.PointerEvent) => e.stopPropagation();

  // Fresh accounts have no workspaces yet — show a create prompt instead.
  if (!ws) {
    return (
      <div className="w-[330px] cursor-pointer" onClick={() => openApp("workspaces")}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium text-app-2">
            <FolderGit2 size={14} style={{ color: accent }} /> Workspaces
          </div>
          <span className="text-[11px] text-app-2 tabular-nums">0 total</span>
        </div>
        <div className="rounded-xl border border-dashed border-app bg-surface-2/40 p-4 text-center">
          <div className="text-sm font-semibold text-app">No workspaces yet</div>
          <div className="mt-1 text-xs text-app-2">
            A workspace pairs a repo and branch with a cloud dev environment.
          </div>
          <button
            onPointerDownCapture={stop}
            onClick={(e) => {
              e.stopPropagation();
              openApp("workspaces");
              setWsWizard(true);
            }}
            className="mt-3 inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
            style={{ background: accent }}
          >
            <Plus size={12} /> New workspace
          </button>
          {sampleCta && (
            <button
              onPointerDownCapture={stop}
              onClick={(e) => {
                e.stopPropagation();
                sampleCta.run();
              }}
              className="mt-1.5 inline-flex items-center justify-center gap-1 rounded-lg border border-app px-3 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
            >
              <FlaskConical size={12} /> {sampleCta.label}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[330px] cursor-pointer" onClick={() => openApp("workspaces")}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-app-2">
          <FolderGit2 size={14} style={{ color: accent }} /> Workspaces
        </div>
        <span className="text-[11px] text-app-2 tabular-nums">{workspaces.length} total</span>
      </div>

      {/* active workspace */}
      <div className="rounded-xl border border-app bg-surface-2/40 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm font-semibold text-app">{ws.repo}</span>
              {ws.isSample && <SampleBadge />}
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-xs text-app-2">
              <GitBranch size={11} />
              <span className="truncate">{ws.branch}</span>
            </div>
          </div>
          <Badge tone={WS_STATUS_TONE[ws.status]}>
            <Dot tone={WS_STATUS_TONE[ws.status]} /> {WS_STATUS_LABEL[ws.status]}
          </Badge>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-surface/60 py-1.5">
            <div className="flex items-center justify-center gap-1 text-sm font-semibold text-app tabular-nums">
              <Cpu size={12} style={{ color: accent }} /> {ws.activeAgents}
            </div>
            <div className="text-[10px] text-app-2">agents</div>
          </div>
          <div className="rounded-lg bg-surface/60 py-1.5">
            <div className="flex items-center justify-center gap-1 text-sm font-semibold text-app tabular-nums">
              <ListTodo size={12} style={{ color: accent }} /> {ws.openTasks}
            </div>
            <div className="text-[10px] text-app-2">tasks</div>
          </div>
          <div className="rounded-lg bg-surface/60 py-1.5">
            <div className="text-sm font-semibold text-app">{WS_ENV_LABEL[ws.env]}</div>
            <div className="text-[10px] text-app-2">env</div>
          </div>
        </div>

        <div className="mt-2.5 flex items-center justify-between text-[11px] text-app-2">
          <span className="flex items-center gap-1">
            <ShieldCheck size={11} className="text-emerald-500" /> Verified {ws.lastVerified}
          </span>
          <span className="capitalize">{ws.provider}</span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-1.5">
        <button
          onPointerDownCapture={stop}
          onClick={(e) => {
            e.stopPropagation();
            openApp("workspaces");
          }}
          className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <ArrowLeftRight size={12} /> Switch
        </button>
        <button
          onPointerDownCapture={stop}
          onClick={(e) => {
            e.stopPropagation();
            openApp("workspaces");
            setWsWizard(true);
          }}
          className="flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
          style={{ background: accent }}
        >
          <Plus size={12} /> New
        </button>
        <button
          onPointerDownCapture={stop}
          onClick={(e) => {
            e.stopPropagation();
            workspaceAction(activeId, "resume");
          }}
          className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <Play size={12} /> Resume
        </button>
      </div>

      {/* subtle live agents shimmer */}
      {ws.activeAgents > 0 && (
        <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-app-2">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: accent }} />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
          </span>
          <motion.span
            initial={{ opacity: 0.4 }}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2.4, repeat: Infinity }}
          >
            {ws.agents[0]?.task ?? "agents working"}
          </motion.span>
        </div>
      )}
    </div>
  );
}
